import { useState, useEffect } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)
      setLoading(false)
    }

    getSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)

        // Only log actual login events from password or magic link
        // Skip token refresh, initial load, etc.
        if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && session?.user) {
          // Only log if this looks like a fresh sign-in
          // We'll use a session storage flag to prevent duplicate logging
          const sessionKey = `login_logged_${session.user.id}`
          const alreadyLogged = sessionStorage.getItem(sessionKey)

          if (!alreadyLogged && event === 'SIGNED_IN') {
            sessionStorage.setItem(sessionKey, 'true')
            // Use setTimeout to avoid blocking the auth flow
            setTimeout(async () => {
              try {
                const authMethod = sessionStorage.getItem('auth_method') || 'email'
                sessionStorage.removeItem('auth_method')
                const { error } = await supabase.from('user_login_logs').insert({
                  user_id: session.user.id,
                  user_agent: navigator.userAgent,
                  auth_provider: authMethod,
                  ip_address: null
                })
                if (error) {
                  console.debug('Login logging failed (this is ok):', error.message)
                }
              } catch (err) {
                console.debug('Login logging error (this is ok):', err)
              }
            }, 500)
          }
        }

        // Clear the flag on sign out
        if (event === 'SIGNED_OUT') {
          sessionStorage.clear()
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signInWithMagicLink = async (email: string) => {
    // Check if this specific email exists in auth.users by trying to sign in
    // with shouldCreateUser: false to prevent new user creation
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
        shouldCreateUser: false // This prevents creating new users
      }
    })

    // If shouldCreateUser is false and user doesn't exist, Supabase will return an error
    if (error && (error.message.includes('User not found') || error.message.includes('Signups not allowed for otp'))) {
      return {
        error: {
          message: 'Unrecognised email address'
        }
      }
    }

    return { error }
  }

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) {
      // Handle specific error cases
      if (error.message.includes('Invalid login credentials') || error.message.includes('Email not confirmed')) {
        return {
          error: {
            message: 'Invalid email or password'
          }
        }
      }
      return {
        error: {
          message: error.message
        }
      }
    }

    return { error: null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })

    if (error) {
      return {
        error: {
          message: error.message
        }
      }
    }

    return { error: null }
  }

  const getAccessToken = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token
  }

  const getSessionTokens = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return {
      accessToken: session?.access_token,
      refreshToken: session?.refresh_token
    }
  }

  const checkAuthMethods = async (email: string) => {
    const response = await fetch('/.netlify/functions/check-auth-methods', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to check auth methods')
    return data as { exists: boolean; hasPhone: boolean; maskedPhone: string | null; hasPassword: boolean }
  }

  const sendSmsOtp = async (email: string) => {
    const response = await fetch('/.netlify/functions/send-sms-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to send SMS code')
    return data as { success: boolean; maskedPhone: string }
  }

  const verifySmsOtp = async (email: string, code: string) => {
    const response = await fetch('/.netlify/functions/verify-sms-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Invalid code')

    // Flag for login logging so we can distinguish SMS OTP from email magic link
    sessionStorage.setItem('auth_method', 'sms_otp')

    // Use the hashed_token to create a session via Supabase
    const { error } = await supabase.auth.verifyOtp({
      token_hash: data.hashed_token,
      type: 'email'
    })

    if (error) throw new Error(error.message)
    return { success: true }
  }

  return {
    user,
    loading,
    signInWithMagicLink,
    signInWithPassword,
    signOut,
    resetPassword,
    getAccessToken,
    getSessionTokens,
    checkAuthMethods,
    sendSmsOtp,
    verifySmsOtp
  }
}
